import { useEffect } from "react";
import { Link, useParams } from "react-router";
import { samplePulses } from "@/data/landing/samplePulses";
import type { DailyPulse, PulseStory } from "@altair/shared/types";
import { resolveStorySlug } from "@/lib/news/slug";
import { readNewsHydrationData } from "@/lib/news/hydration";
import { FullPulseView } from "@/components/news/FullPulseView";
import { SingleStoryView } from "@/components/news/SingleStoryView";

/**
 * Thin orchestrator for the three `/news/:date[/:slug]` entry points:
 *
 * 1. List-mode (`/news/:date`, no focusStory) → <FullPulseView />
 * 2. Single-story (`/news/:date/:slug`, focusStory set) → <SingleStoryView />
 * 3. Not-found fallback (unknown date or slug) → inline NotFound
 *
 * SSR hydrates data from the `#__news-data__` JSON element ({ pulse,
 * allPulses, storySlug }) — see lib/news/hydration. The SPA/dev path reads from
 * the bundled `samplePulses` instead. Both pull from the same `DailyPulse`
 * shape so downstream components don't need to care which source served them.
 */

interface InsightDetailPageProps {
  /** Explicit pulse to render. Preferred over SSR + static fallback. */
  pulse?: DailyPulse;
  /**
   * When set, switches to single-story mode and renders only this story.
   * Must be one of `pulse.stories` (no cross-pulse stories).
   */
  focusStory?: PulseStory;
  /**
   * Recent pulse dates for prev/next navigation (only `.date` is read).
   * Defaults to `samplePulses` in dev.
   */
  allPulses?: Pick<DailyPulse, "date">[];
}

function NotFound() {
  return (
    <div className="min-h-dvh flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-xl font-semibold">Issue not found</h2>
        <Link
          to="/news"
          className="mt-4 inline-block text-sm"
          style={{ color: "hsl(var(--foreground) / 0.50)" }}
          onClick={(e) => {
            // Force full-page nav so news-renderer SSR serves /news with
            // fresh DB-backed content. See LandingNavbar / Footer for the
            // companion call sites.
            e.preventDefault();
            window.location.href = "/news";
          }}
        >
          Back to Billing News
        </Link>
      </div>
    </div>
  );
}

export default function InsightDetailPage(props: InsightDetailPageProps = {}) {
  const params = useParams<{ date: string; slug?: string }>();
  const ssr = readNewsHydrationData();

  const staticFallback = samplePulses as DailyPulse[];
  const allPulses: Pick<DailyPulse, "date">[] =
    props.allPulses ?? ssr?.allPulses ?? staticFallback;

  const pulse: DailyPulse | undefined =
    props.pulse ??
    ssr?.pulse ??
    staticFallback.find((p) => p.date === params.date);

  const focusStory: PulseStory | undefined =
    props.focusStory ??
    ssr?.focusStory ??
    (params.slug && pulse
      ? pulse.stories.find((s) => resolveStorySlug(s) === params.slug)
      : undefined);

  useEffect(() => {
    document.documentElement.classList.remove("dark");
    // Scroll handling: if we're in single-story mode, just go to the top.
    // In list mode, respect a `#story-*` hash from legacy share links.
    if (focusStory) {
      if (typeof window !== "undefined") window.scrollTo(0, 0);
      return;
    }
    if (typeof window !== "undefined" && window.location.hash) {
      setTimeout(() => {
        const el = document.querySelector(window.location.hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
  }, [params.date, params.slug, focusStory]);

  if (!pulse) return <NotFound />;
  // A :slug was requested but we couldn't resolve it — treat as 404 rather
  // than silently falling back to list mode.
  if (params.slug && !focusStory) return <NotFound />;

  return focusStory ? (
    <SingleStoryView pulse={pulse} story={focusStory} />
  ) : (
    <FullPulseView pulse={pulse} allPulses={allPulses} />
  );
}
